'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import api from '@/utils/api';
import { RiMenuLine, RiCloseLine } from 'react-icons/ri';
import { MdOutlineLogout } from 'react-icons/md';
import { MAIN_NAV, SYSTEM_NAV } from '@/constants';
import NotificationDot from '../ui/NotificationDot';
import LogoIcon from '../ui/LogoIcon';
import SectionLabel from '../ui/SectionLabel';
import Avatar from '../ui/Avatar';
import UserMenuList from '../ui/UserMenuList';
import UserCardTablet from '../ui/UserCardTablet';
import FullNavItem from '../ui/FullNavItem';
import IconNavItem from '../ui/IconNavItem';
import NavGroup from '../ui/NavGroup';
import { LogoutConfirmationModal } from '../modals/LogoutConfirmationModal';

export function useClickOutside(
  ref: React.RefObject<HTMLElement | null>,
  onClose: () => void,
  enabled = true,
) {
  useEffect(() => {
    if (!enabled) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler, { passive: true });
    return () => document.removeEventListener('mousedown', handler);
  }, [ref, onClose, enabled]);
}

NotificationDot.displayName = 'NotificationDot';
LogoIcon.displayName = 'LogoIcon';
SectionLabel.displayName = 'SectionLabel';
Avatar.displayName = 'Avatar';
UserMenuList.displayName = 'UserMenuList';
UserCardTablet.displayName = 'UserCardTablet';
FullNavItem.displayName = 'FullNavItem';
IconNavItem.displayName = 'IconNavItem';
NavGroup.displayName = 'NavGroup';

export const Sidebar: React.FC = () => {
  const router = useRouter();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);

  const handleLogout = useCallback(async () => {
    setLogoutLoading(true);
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setLogoutLoading(false);
      setIsLogoutModalOpen(false);
      router.push('/login');
      router.refresh();
    }
  }, [router]);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  return (
    <>
      <aside
        aria-label="Main navigation"
        className="hidden lg:flex w-70 bg-[#0f1f4b] fixed inset-y-0 left-0 flex-col z-30"
      >
        <div className="flex items-center p-6 border-b border-white/10 shrink-0">
          <img
            src="/Assets/agar_mart_logo.svg"
            alt="AgarMart Logo"
            className="h-18 w-auto"
          />
        </div>

        <nav className="flex-1 overflow-y-auto py-6 space-y-6">
          <NavGroup label="Main Menu" items={MAIN_NAV} />
          <NavGroup label="System" items={SYSTEM_NAV} />
        </nav>

        <div className="p-4 border-t border-white/10 shrink-0">
          <button
            onClick={() => setIsLogoutModalOpen(true)}
            className="cursor-pointer w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border-2 border-red-500 text-red-500 hover:bg-red-500/10 transition-colors font-semibold text-sm"
            title="Logout"
          >
            <MdOutlineLogout size={18} />
            LOGOUT
          </button>
        </div>
      </aside>

      <aside
        aria-label="Main navigation"
        className="hidden md:flex lg:hidden w-14 bg-[#0f1f4b] fixed inset-y-0 left-0 flex-col items-center py-4 z-30"
      >
        <img
          src="/Assets/agar_mart_logo.svg"
          alt="AgarMart Logo"
          className="h-10 w-auto"
        />
        <div className="w-1 h-1 rounded-full bg-slate-600 my-3 shrink-0" />

        <nav className="flex-1 w-full px-2">
          {MAIN_NAV.map(item => <IconNavItem key={item.href} item={item} />)}
          <div className="w-1 h-1 rounded-full bg-slate-600 mx-auto my-2" />
          {SYSTEM_NAV.map(item => <IconNavItem key={item.href} item={item} />)}
        </nav>

        <div className="p-3 border-t border-white/10 shrink-0">
          <button
            onClick={() => setIsLogoutModalOpen(true)}
            className="cursor-pointer w-full flex items-center justify-center p-2 rounded-lg border-2 border-red-500 text-red-500 hover:bg-red-500/10 transition-colors"
            title="Logout"
          >
            <MdOutlineLogout size={18} />
          </button>
        </div>
      </aside>

      <header className="md:hidden fixed top-0 inset-x-0 h-14 bg-[#0f1f4b] flex items-center justify-between px-4 z-40">
        <div className="flex items-center gap-2">
          <img
            src="/Assets/agar_mart_logo.svg"
            alt="AgarMart Logo"
            className="h-8 w-auto"
          />
        </div>
        <button
          type="button"
          aria-label="Open navigation"
          onClick={openDrawer}
          className="text-white p-1 cursor-pointer"
        >
          <RiMenuLine size={22} />
        </button>
      </header>

      <div
        aria-hidden
        onClick={closeDrawer}
        className={`
          md:hidden fixed inset-0 bg-black/50 z-40
          transition-opacity duration-300
          ${drawerOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
        `}
      />

      <aside
        aria-label="Mobile navigation"
        aria-hidden={!drawerOpen}
        className={`
          md:hidden fixed top-0 left-0 h-full w-65
          bg-[#0f1f4b] z-50 flex flex-col
          transition-transform duration-300 ease-in-out
          ${drawerOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="flex items-center p-5 border-b border-white/10 shrink-0">
          <div className="flex items-center gap-2">
            <img
              src="/Assets/agar_mart_logo.svg"
              alt="AgarMart Logo"
              className="h-8 w-auto"
            />
          </div>
          <button
            type="button"
            aria-label="Close navigation"
            onClick={closeDrawer}
            className="ml-auto text-slate-400 hover:text-white transition-colors cursor-pointer p-0.5"
          >
            <RiCloseLine size={20} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-5 space-y-6">
          <NavGroup label="Main Menu" items={MAIN_NAV} onItemClick={closeDrawer} />
          <NavGroup label="System" items={SYSTEM_NAV} onItemClick={closeDrawer} />
        </nav>

        <div className="p-4 border-t border-white/10 shrink-0">
          <button
            onClick={() => setIsLogoutModalOpen(true)}
            className="cursor-pointer w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border-2 border-red-500 text-red-500 hover:bg-red-500/10 transition-colors font-semibold text-sm"
            title="Logout"
          >
            <MdOutlineLogout size={18} />
            LOGOUT
          </button>
        </div>
      </aside>

      <LogoutConfirmationModal
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        onConfirm={handleLogout}
        loading={logoutLoading}
      />
    </>
  );
};
